import React, { useState, useEffect, useCallback } from 'react';
import { Search, AlertTriangle, Eye, RotateCcw, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import api from '../../../services/api';

const ERROR_MODE_LABELS = {
    'never': { fr: 'Jamais réussies', en: 'Never correct' },
    'sometimes': { fr: 'Erreurs ponctuelles', en: 'Occasional errors' },
    'recent': { fr: 'Erreurs récentes', en: 'Recent errors' },
};

const REVISION_LABELS = {
    30: { fr: '30 jours', en: '30 days' },
    60: { fr: '60 jours', en: '60 days' },
    90: { fr: '90 jours', en: '90 days' },
};

const ITEMS_PER_PAGE = 5;

const CustomExamHistorySection = ({ levelId, language, onReviewSession, onRestartSession, lastActiveId = null }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);

    useEffect(() => {
        setLoading(true);
        setPage(0);
        api.getCustomExamHistory(levelId)
            .then(setHistory)
            .catch(() => setHistory([]))
            .finally(() => setLoading(false));
    }, [levelId]);

    const totalPages = Math.ceil(history.length / ITEMS_PER_PAGE);
    const pageItems = history.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '0:00';
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    const getConfigSummary = useCallback((entry) => {
        const config = entry.config;
        if (entry.examType === 'keyword') {
            const kws = config?.keywords?.join(', ') || '?';
            const op = config?.operator || 'OR';
            return `${kws} (${op})`;
        }
        if (entry.examType === 'error') {
            const modeLabel = ERROR_MODE_LABELS[config?.errorMode]?.[language] || config?.errorMode;
            return modeLabel;
        }
        if (entry.examType === 'revision') {
            const days = config?.daysThreshold;
            const label = REVISION_LABELS[days]?.[language];
            return label || (language === 'fr' ? `${days} jours` : `${days} days`);
        }
        return '?';
    }, [language]);

    const getScoreColor = (score) => {
        if (score >= 75) return 'text-emerald-600 dark:text-emerald-400';
        if (score >= 50) return 'text-orange-600 dark:text-orange-400';
        return 'text-red-600 dark:text-red-400';
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <h2 className="text-lg font-black text-gray-400 dark:text-white ml-2 text-xs uppercase tracking-widest opacity-60 dark:opacity-40">
                    {language === 'fr' ? 'Historique sur mesure' : 'Custom history'}
                </h2>
                <div className="bg-white dark:bg-[#121212] rounded-2xl p-6 border border-gray-200 dark:border-white/5 text-center">
                    <p className="text-sm text-gray-400 animate-pulse">
                        {language === 'fr' ? 'Chargement...' : 'Loading...'}
                    </p>
                </div>
            </div>
        );
    }

    if (history.length === 0) return null;

    return (
        <div data-help="custom-history" className="space-y-4">
            <h2 className="text-lg font-black text-gray-400 dark:text-white ml-2 text-xs uppercase tracking-widest opacity-60 dark:opacity-40">
                {language === 'fr' ? 'Historique sur mesure' : 'Custom history'}
            </h2>

            <div className="space-y-2">
                {pageItems.map((entry) => {
                    const isActive = lastActiveId != null && entry.id === lastActiveId;
                    return (
                    <div
                        key={entry.id}
                        className={`bg-white dark:bg-[#121212] rounded-2xl p-4 border hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-all duration-200 shadow-sm dark:shadow-none group transform hover:scale-[1.02] hover:shadow-md dark:hover:shadow-lg ${
                            isActive
                                ? 'border-blue-400 dark:border-blue-500/60 ring-2 ring-blue-400/30 dark:ring-blue-500/20'
                                : 'border-gray-200 dark:border-white/5'
                        }`}
                    >
                        {/* Top row: icon + info + score */}
                        <div className="flex items-center gap-3">
                            {/* Icon */}
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg shrink-0 ${
                                entry.examType === 'keyword'
                                    ? 'bg-gradient-to-br from-violet-500 to-blue-500'
                                    : entry.examType === 'revision'
                                        ? 'bg-gradient-to-br from-teal-500 to-cyan-500'
                                        : 'bg-gradient-to-br from-orange-500 to-red-500'
                            }`}>
                                {entry.examType === 'keyword'
                                    ? <Search className="w-4 h-4" />
                                    : entry.examType === 'revision'
                                        ? <Clock className="w-4 h-4" />
                                        : <AlertTriangle className="w-4 h-4" />
                                }
                            </div>

                            {/* Info */}
                            <div className="min-w-0 flex-1">
                                <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                    {getConfigSummary(entry)}
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {entry.questionCount}q
                                    {' · '}{formatDuration(entry.duration)}
                                    {' · '}{formatDate(entry.date)}
                                </p>
                            </div>

                            {/* Score */}
                            <div className={`text-lg font-black shrink-0 ${getScoreColor(entry.score)}`}>
                                {entry.score}%
                            </div>
                        </div>

                        {/* Bottom row: action buttons */}
                        <div className="flex items-center gap-1.5 mt-3 justify-end">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onReviewSession(entry.id);
                                }}
                                className="px-3 py-1.5 rounded-xl text-xs font-bold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10 group-hover:bg-gray-100 dark:group-hover:bg-white/10 hover:bg-gray-100 dark:hover:bg-white/10 transition shrink-0 flex items-center gap-1.5"
                            >
                                <Eye className="w-3.5 h-3.5" />
                                {language === 'fr' ? 'Revoir' : 'Review'}
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRestartSession(entry);
                                }}
                                className="px-3 py-1.5 rounded-xl text-xs font-bold text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition shrink-0 flex items-center gap-1.5"
                            >
                                <RotateCcw className="w-3.5 h-3.5" />
                                {language === 'fr' ? 'Recommencer' : 'Restart'}
                            </button>
                        </div>
                    </div>
                    );
                })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3">
                    <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 disabled:opacity-30 transition"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                        {page + 1} / {totalPages}
                    </span>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 disabled:opacity-30 transition"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default CustomExamHistorySection;
